/**
 * <cm-product-media>
 *
 * Lightweight custom-element controller for the cm-product-media block.
 * Handles:
 *   - Click on thumbnail → swap the active slide on the main stage
 *   - Keyboard navigation across thumbnails (Arrow / Home / End)
 *   - Variant change events on the page → re-pick the active media when
 *     the selected variant has a featured image, matching the section
 *     theme's existing variant:change event.
 *
 * The DOM model is fully rendered server-side (every slide is in the
 * DOM, hidden via aria-current). We only toggle aria-current/-selected
 * here — CSS does the rest.
 */

class CmProductMedia extends HTMLElement {
  constructor() {
    super();
    this._onThumbClick = this._onThumbClick.bind(this);
    this._onThumbKeydown = this._onThumbKeydown.bind(this);
    this._onVariantChange = this._onVariantChange.bind(this);
  }

  connectedCallback() {
    this.stage = this.querySelector('[data-cm-stage]');
    this.thumbContainer = this.querySelector('[data-cm-thumbs]');
    this.slides = Array.from(this.querySelectorAll('.cm-product-media__slide'));
    this.thumbs = this.thumbContainer ? Array.from(this.thumbContainer.querySelectorAll('.cm-product-media__thumb')) : [];

    if (this.thumbs.length === 0) return;

    this.thumbs.forEach((t) => {
      t.addEventListener('click', this._onThumbClick);
      t.addEventListener('keydown', this._onThumbKeydown);
    });

    // Listen for the theme's variant update event so the active slide
    // tracks the currently selected variant where possible.
    document.addEventListener('variant:update', this._onVariantChange);
  }

  disconnectedCallback() {
    this.thumbs.forEach((t) => {
      t.removeEventListener('click', this._onThumbClick);
      t.removeEventListener('keydown', this._onThumbKeydown);
    });
    document.removeEventListener('variant:update', this._onVariantChange);
  }

  _onThumbClick(event) {
    const target = event.currentTarget;
    const idx = Number(target.dataset.index);
    if (Number.isNaN(idx)) return;
    this._setActiveIndex(idx);
  }

  _onThumbKeydown(event) {
    const keys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (!keys.includes(event.key)) return;

    event.preventDefault();
    const current = Number(event.currentTarget.dataset.index);
    let next = current;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        next = current + 1;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        next = current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = this.thumbs.length - 1;
        break;
    }

    if (next < 0) next = this.thumbs.length - 1;
    if (next >= this.thumbs.length) next = 0;

    this._setActiveIndex(next);
    const nextThumb = this.thumbs[next];
    if (nextThumb) nextThumb.focus();
  }

  _onVariantChange(event) {
    // VariantUpdateEvent dispatches { detail: { resource: variant, ... } }.
    const variant = event && event.detail && event.detail.resource;
    if (!variant || !variant.featured_media || !variant.featured_media.id) return;

    const id = String(variant.featured_media.id);
    const match = this.slides.findIndex((slide) => slide.dataset.mediaId === id);
    if (match >= 0) this._setActiveIndex(match);
  }

  _setActiveIndex(idx) {
    this.slides.forEach((slide, i) => {
      const active = i === idx;
      slide.setAttribute('aria-current', active ? 'true' : 'false');
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    this.thumbs.forEach((thumb, i) => {
      const active = i === idx;
      thumb.setAttribute('aria-selected', active ? 'true' : 'false');
      thumb.setAttribute('tabindex', active ? '0' : '-1');
    });

    // Keep the active thumbnail in view inside its scrolling rail.
    const activeThumb = this.thumbs[idx];
    if (activeThumb && typeof activeThumb.scrollIntoView === 'function') {
      activeThumb.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }
  }
}

if (!customElements.get('cm-product-media')) {
  customElements.define('cm-product-media', CmProductMedia);
}
